// КОНТРОЛЛЕР ГОЛОСА ПАНЕЛИ C — одна запись за раз на весь экран. Г-4 `3e619875cbb88188acf1f641ee43a3ec`.
//
// Что он делает (номера — пункты задания):
//   1. Режим 3 (Г-2): ОДИН `MediaRecorder` на запись, куски по 10 с (`start(timeslice)`), к кускам со
//      второго приклеен заголовок первого (`framing.js`) → вызов `chunk`. Тихие куски не шлём (`s988.d5`).
//      ⚠️ Резать по паузам через `requestData()` НЕ стал: на iOS не проверено, а режим 3 проверен именно
//      с `timeslice` — куски остаются по 10 с (разрешено заданием, названо в сдаче).
//   2. «Стоп» → склейка всех кусков = целый файл → вызов `final`; пока ждём — фаза `finishing`
//      («Finishing…», `s994.d3`). Вставку и правило «правку не затираем» делает вызывающий (`voiceText.js`).
//   3. Нажатие (`s908.d3`): запись стартует на нажатии; отпустили быстрее 0,5 с — тап, запись идёт до
//      следующего нажатия; держали — отпускание останавливает. Отпускание всегда «стоп и сохранить».
//      Потолок одной записи 5 мин (строка 85; было 3), авто-стоп по 9 с тишины — оба без строки (`s995.d3`);
//      тишина = нет 3 тиков подряд ≥ порога (строка 81, `VOICE_RUN_TICKS`).
//   5. Разрешение — состояние кнопки (`s994.d2`): фаза `asking`, пока висит системное окно. Нет устройства,
//      небезопасный адрес → `unavailable` на всю панель (`s517.d1`). Отказ человека — покой, микрофоны живы;
//      отказ без окна — строка под полем (`micOff`, строка 83, `s1001.d1`).
//   6. Ответ лимита (Г-3, `403 voice_limit`) → `limit`: гаснут микрофоны, поле живое (`s994.d1`).
//
// ⭐ Повторы куска 4 с × 2 + 0,5 с (`chunkWithRetry`), финал — одна попытка (`s995.d1`, тикет
// `3c219875cbb881cc9f31d19a4fb24db7`). Дыра в черновике (кусок сдался) отдаётся с финалом — строку сбоя у поля
// рисует экран, только если финал не пришёл.
// ⛔ Звук нигде не хранится: куски живут в памяти до конца записи и отпускаются.
//
// ⭐ СТОРОЖ ПОКОЯ (проход Кирилла на iPhone, s999, 16.10): у Safari после авто-стопа по тишине голос
// повис — микрофоны выключены, текста нет, до перезагрузки. Фаза `finishing` выключает ВСЕ микрофоны
// экрана (`busyElsewhere`), а выход из неё держался только на событиях рекордера (`onstop` → `finish`).
// Теперь на входе в `finishing` взводится таймер по ЧАСАМ: таймаут финала + `FINISH_GRACE_MS`. Не вернулись
// в покой к этому сроку — покой силой: пришедшие черновики остаются в поле, поздние ответы этой записи
// больше ничего не трогают (поколение сдвигается). Ни один путь остановки не держит голос дольше.
//
// Все браузерные возможности приходят через `deps` — контроллер гоняется в юнитах без браузера.

import { containerOf, initSegment, standalone } from './framing.js';
import { chunkWithRetry, finalWaitMs, MAX_RECORDING_S, transcribe as defaultTranscribe } from './transcribeClient.js';

export const CHUNK_MS = 10_000;
export const HOLD_MS = 500;
export const MAX_RECORDING_MS = MAX_RECORDING_S * 1000;
export const SILENCE_STOP_MS = 9_000;
export const TICK_MS = 100;
/**
 * Битрейт записи (строка 84, тикет `3fe19875cbb881a18296e848a98307de`). По умолчанию Safari на iPhone пишет AAC
 * ~189 кбит/с → финал длиннее ~177 с больше 4 МБ сервера (`413`). Замер на iPhone 19.10 (стенд `voice-bitrate`,
 * три рекордера на одном потоке): настройку Safari уважает (64,7–64,8 кбит/с по факту); речь 62 с — текст Deepgram
 * слово в слово как на 192k. 3 мин = 1,39 МБ, 5 мин ≈ 2,32 МБ — под 4 МБ с запасом. Chrome/Firefox: Opus 64k.
 */
export const AUDIO_BPS = 64_000;
/** Запас сторожа покоя сверх таймаута финала: на склейку файла и разбор ответа. */
export const FINISH_GRACE_MS = 1_000;
/**
 * Порог «здесь голос»: RMS сигнала ПОСЛЕ обработки браузера (шумодав и АРУ `getUserMedia` включены по
 * умолчанию). Замер Г-4 в Chromium на записи Г-2 (40 с речи): паузы 0,001–0,002, речь p50 0,028 / p90 0,12,
 * самая длинная пауза внутри речи 1,2 с; ровный шум (тон) шумодав давит до ~0,005. Порог 0,01 — в 5 раз
 * выше паузы и выше задавленного шума. iPhone/Safari — проверка на проходе Кирилла.
 */
export const VOICE_RMS = 0.01;
/**
 * Голос для авто-стопа = звук, который длится: ≥ 3 тика ПОДРЯД ≥ `VOICE_RMS` (строка 81, тикет
 * `3fe19875cbb881a7a49ac5d4c83307bb`). Замер iPhone 19.10: фон комнаты ~0,002, но одиночные всплески 0,044–0,077
 * (шорох, касание) по одному тику обнуляли счёт 9 с — 41 с тишины без стопа. Офлайн-прогон (cc-223): речь Г-2 на
 * уровне iPhone (p50 0,0105) — самая длинная пауза голоса 4,3 с из 9, ни одного обрыва; тишина со всплесками по
 * 1–2 тика — стоп на 9,0 с. ⚠️ Флаги «тихий кусок не шлём» (`chunkVoiced`/`anyVoiced`) остаются на одиночном
 * тике намеренно: короткое слово (< 300 мс) не должно пропасть из текста.
 */
export const VOICE_RUN_TICKS = 3;

export const PHASE = { IDLE: 'idle', ASKING: 'asking', RECORDING: 'recording', FINISHING: 'finishing' };

/**
 * Отказ человека в доступе (строка 83, `s1001.d1` — меняет `s517.d1`): НЕ `unavailable`. Микрофоны остаются живыми,
 * следующий тап снова спрашивает.
 */
export function isDeniedError(err) {
  return ['NotAllowedError', 'PermissionDeniedError'].includes(err?.name || '');
}

/** Отказ `getUserMedia` → класс `s517.d1`: `unavailable` (не чинится в визарде) или молчаливый срыв попытки. */
export function isUnavailableError(err) {
  const name = err?.name || '';
  return ['SecurityError', 'NotFoundError', 'DevicesNotFoundError', 'OverconstrainedError', 'TypeError'].includes(name);
}

/**
 * Отказал ли браузер БЕЗ окна. Замер на симуляторе iOS 26.5 (cc-223): «Cancel» в окне Safari → `permissions.query`
 * = `prompt`, и следующий тап снова показывает окно (проверено 3 раза подряд); запрет настройкой (Safari → Microphone
 * → Deny) → `denied`, окна нет, а отказ приходит через 0,8–1,0 с — по времени его не отличить от быстрого «Cancel».
 * Поэтому критерий — состояние разрешения, не время. Всё, кроме `prompt` (`denied`, `granted` при отказе = запрет
 * системы, API нет или бросил), — «без окна»: молча мёртвая кнопка хуже строки (оговорка web-Claude).
 *
 * 🔴 ПОПРАВКА ПРОХОДОМ КИРИЛЛА 21.10 (iPhone, iOS 18.7): после ВТОРОГО «Нет» Safari перестал показывать окно и молча
 * отклонял каждый тап — даже после перезагрузки частной вкладки; строки при этом не было, кнопки молчали. Значит на
 * iPhone `query` в этом состоянии либо отвечает `prompt`, либо не отвечает вовсе (трассы не было — не различить).
 * Поэтому две страховки: ответ `query` ждём не дольше `PERMISSION_QUERY_MS` (нет ответа = без окна), а второй отказ
 * подряд даёт строку при любом ответе (`DENIALS_FOR_LINE`).
 */
async function refusedWithoutPrompt(env, setTimeoutFn) {
  try {
    const q = env.navigator?.permissions?.query?.({ name: 'microphone' });
    const p = await Promise.race([q, new Promise((r) => { setTimeoutFn(() => r('timeout'), PERMISSION_QUERY_MS); })]);
    if (p === 'timeout') return true;
    return p?.state !== 'prompt';
  } catch {
    return true;
  }
}

/** Сколько ждём ответа `permissions.query` после отказа (строка 83, проход 21.10). */
export const PERMISSION_QUERY_MS = 1_000;
/** Отказов подряд, после которых строка встаёт при любом ответе `query` (строка 83, проход 21.10). */
export const DENIALS_FOR_LINE = 2;

/** Есть ли у страницы голос вообще — до первого тапа (небезопасный адрес / нет API). */
export function voiceSupported(env = globalThis) {
  return Boolean(
    env?.isSecureContext !== false
    && env?.navigator?.mediaDevices?.getUserMedia
    && typeof env?.MediaRecorder === 'function',
  );
}

/** Байты куска. `Blob.arrayBuffer` есть во всех целевых браузерах; `FileReader` — запас для старых движков. */
function bytesOf(blob) {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.readAsArrayBuffer(blob);
  });
}

/**
 * Уровень записи для лога сервера (строка 81, web-Claude 20.10): пик и p90 тиков, 4 знака. Только два числа —
 * ни звука, ни текста. Пик один не годится: в тихой комнате его даёт касание (0,077, замер 19.10), p90 — голос.
 * Нет анализатора / тиков — `null`.
 */
export function levelOf(ticks) {
  if (!ticks.length) return null;
  const s = [...ticks].sort((a, b) => a - b);
  const r4 = (v) => Math.round(Math.min(1, Math.max(0, v)) * 10_000) / 10_000;
  return { peak: r4(s[s.length - 1]), p90: r4(s[Math.min(s.length - 1, Math.floor(0.9 * s.length))]) };
}

function rmsOf(analyser, buf) {
  if (analyser.getFloatTimeDomainData) {
    analyser.getFloatTimeDomainData(buf);
    let s = 0;
    for (let i = 0; i < buf.length; i++) s += buf[i] * buf[i];
    return Math.sqrt(s / buf.length);
  }
  const b = new Uint8Array(buf.length);
  analyser.getByteTimeDomainData(b);
  let s = 0;
  for (let i = 0; i < b.length; i++) { const v = (b[i] - 128) / 128; s += v * v; }
  return Math.sqrt(s / b.length);
}

/**
 * @param {object} opts
 * @param {(s: object) => void} opts.onState  — снимок: { phase, field, startedAt, unavailable, limit, limitField }
 * @param {(field: string, text: string) => void} opts.onDraft — черновик куска (по порядку кусков)
 * @param {(field: string, text: string|null, info: {hole: boolean}) => void} opts.onFinal — финал (`null` — не пришёл /
 *   пустая запись); `hole` — кусок сдался, а финал не закрыл дыру (строка сбоя у поля)
 * @param {() => {pass: string, stages: number}} opts.context — id прохода и число этапов на момент вызова
 * @param {object} [opts.deps]
 */
export function createVoice({ onState, onDraft, onFinal, onStart = () => {}, context, deps = {} }) {
  const env = deps.env ?? globalThis;
  const now = deps.now ?? (() => Date.now());
  const setIntervalFn = deps.setInterval ?? ((f, ms) => setInterval(f, ms));
  const clearIntervalFn = deps.clearInterval ?? ((id) => clearInterval(id));
  const setTimeoutFn = deps.setTimeout ?? ((f, ms) => setTimeout(f, ms));
  const clearTimeoutFn = deps.clearTimeout ?? ((id) => clearTimeout(id));
  /** Крючок трассы событий (без текста и звука). Трасса превью снята при вливе (cc-220); по умолчанию — пусто. */
  const trace = deps.trace ?? (() => {});
  const transcribe = deps.transcribe ?? defaultTranscribe;
  const sendChunkReq = (req) => chunkWithRetry(req, { transcribe, ...(deps.sleep ? { sleep: deps.sleep } : {}) });

  const st = {
    phase: PHASE.IDLE, field: null, startedAt: null,
    unavailable: !voiceSupported(env), limit: false, limitField: null,
    micOff: null, // поле, где браузер отказал в доступе без окна (строка 83) — строка под ним
  };
  let rec = null; let stream = null; let ctx = null; let analyser = null; let buf = null; let ticker = null;
  let parts = []; let init = null; let container = null; let mime = '';
  let chunkVoiced = false; let anyVoiced = false; let lastVoiceAt = 0; let loudRun = 0;
  let levels = [];
  let denials = 0; // отказов в доступе подряд (строка 83); сбрасывает удачный `getUserMedia` // уровни тиков записи — для пика и p90 в логе сервера (строка 81: доля тихих голосов в бете)
  let pressAt = 0; let pressed = false; let tapMode = false;
  let chunkJobs = []; let results = []; let nextDraft = 0; let finalApplied = false;
  let hole = false; // хоть один кусок сдался — в черновике дыра
  let gen = 0; // поколение записи: ответы прошлой записи не трогают новую
  let guard = null; // сторож покоя фазы `finishing`

  const emit = () => { trace('state', st.phase, st.field, st.unavailable ? 'unavailable' : '', st.limit ? 'limit' : ''); onState({ ...st }); };
  const set = (patch) => { Object.assign(st, patch); emit(); };

  function release() {
    if (ticker !== null) { clearIntervalFn(ticker); ticker = null; }
    try { stream?.getTracks?.().forEach((t) => t.stop()); } catch { /* уже остановлен */ }
    try { ctx?.close?.(); } catch { /* закрыт */ }
    stream = null; ctx = null; analyser = null; buf = null;
  }

  function flushDrafts(field, myGen) {
    while (results[nextDraft] !== undefined) {
      const r = results[nextDraft];
      nextDraft += 1;
      if (myGen === gen && !finalApplied && r) onDraft(field, r);
    }
  }

  function markLimit(field) {
    if (!st.limit) set({ limit: true, limitField: field });
  }

  function sendChunk(k, bytes, field, myGen) {
    const job = (async () => {
      const { pass, stages } = context();
      const r = await sendChunkReq({ audio: bytes, mimeType: mime, pass, stages });
      if (myGen !== gen) return;
      trace('chunk-reply', k, r.ok ? 'ok' : r.limit ? 'limit' : 'failed');
      if (r.limit) { markLimit(field); if (st.phase === PHASE.RECORDING) stop('limit'); }
      results[k] = r.ok ? r.text : '';
      // Кусок сдался (исчерпал попытки или 4xx) — в черновике дыра; закроет её финал (`s995.d1` п. 5–6).
      if (r.failed) hole = true;
      if (r.ok && Number.isFinite(r.remaining) && r.remaining <= 0) { markLimit(field); if (st.phase === PHASE.RECORDING) stop('limit'); }
      flushDrafts(field, myGen);
    })();
    return job;
  }

  async function onChunk(blob, k, voiced, field, myGen) {
    parts.push(blob);
    // ⚠️ ЧТЕНИЕ БАЙТОВ КУСКА МОЖЕТ УПАСТЬ (WebKit: `NotReadableError` на куске, пришедшем на «стоп», при обрыве сети —
    // стенд разбора 19.10). Без перехвата задача куска падала молча: кусок не уходил, дыра не засчитывалась (строки
    // сбоя нет), а `results[k]` не заполнялся — и черновики следующих кусков вставали в очередь навсегда. Непрочитанный
    // кусок — тот же случай, что несклеенный: дыра, если в нём был голос.
    let u8 = null;
    try { u8 = new Uint8Array(await bytesOf(blob)); } catch (err) { trace('chunk-read-error', k, err?.name || ''); }
    if (u8 && k === 0) init = initSegment(u8, container);
    // Последний кусок (пришёл на «стоп») ТОЖЕ уходит черновиком, с теми же повторами (`s995.d1` п. 2): не придёт
    // финал — без него в тексте не было бы последних секунд.
    const piece = u8 && voiced && !st.limit ? standalone(u8, k, init, container) : null;
    trace('chunk', k, u8?.length ?? -1, voiced ? 'voiced' : 'silent', piece ? 'send' : 'skip');
    if (piece) await sendChunk(k, piece, field, myGen);
    else {
      // Голосовой кусок, который не удалось склеить в файл (нет фрагмента), — тоже дыра в черновике; тихий и
      // отрезанный лимитом — нет.
      if (voiced && !st.limit) hole = true;
      results[k] = '';
      flushDrafts(field, myGen);
    }
  }

  function tick() {
    if (st.phase !== PHASE.RECORDING) return;
    const t = now();
    if (analyser) {
      const rms = rmsOf(analyser, buf);
      levels.push(rms);
      if (rms >= VOICE_RMS) {
        chunkVoiced = true; anyVoiced = true;
        loudRun += 1;
        if (loudRun >= VOICE_RUN_TICKS) lastVoiceAt = t;
      } else loudRun = 0;
      if (t - lastVoiceAt >= SILENCE_STOP_MS) { stop('silence'); return; }
    }
    if (t - st.startedAt >= MAX_RECORDING_MS) stop('cap');
  }

  async function start(field) {
    if (st.unavailable || st.limit || st.phase !== PHASE.IDLE) return;
    gen += 1;
    const myGen = gen;
    parts = []; init = null; chunkJobs = []; results = []; nextDraft = 0; finalApplied = false; hole = false;
    chunkVoiced = false; anyVoiced = false; loudRun = 0; levels = []; tapMode = false;
    // Новая попытка снимает строку «микрофон выключен» (в любом поле): откажут снова — встанет у этого.
    set({ phase: PHASE.ASKING, field, micOff: null });

    // AudioContext — ДО первого `await`: на iOS его можно создать/разбудить только внутри жеста.
    const AC = env.AudioContext || env.webkitAudioContext;
    try { ctx = AC ? new AC() : null; } catch { ctx = null; }

    try {
      stream = await env.navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      trace('gum-error', err?.name || '');
      release();
      if (myGen !== gen) return;
      if (isDeniedError(err)) {
        denials += 1;
        const quiet = denials >= DENIALS_FOR_LINE || await refusedWithoutPrompt(env, setTimeoutFn);
        trace('denied', denials, quiet ? 'no-prompt' : 'prompt');
        if (myGen !== gen) return;
        set({ phase: PHASE.IDLE, field: null, micOff: quiet ? field : null });
      } else if (isUnavailableError(err)) set({ phase: PHASE.IDLE, field: null, unavailable: true });
      else set({ phase: PHASE.IDLE, field: null });
      return;
    }
    denials = 0;
    if (myGen !== gen || st.phase !== PHASE.ASKING) { release(); return; }
    try {
      stream.getTracks?.().forEach((tr) => ['ended', 'mute', 'unmute'].forEach((e) => tr.addEventListener?.(e, () => trace('track', e))));
    } catch { /* трасса не обязательна */ }

    try {
      if (ctx) {
        await ctx.resume?.();
        const src = ctx.createMediaStreamSource(stream);
        analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        src.connect(analyser);
        buf = new Float32Array(analyser.fftSize);
      }
    } catch { analyser = null; }
    trace('audio', ctx?.state ?? 'none', analyser ? 'analyser' : 'no-analyser');
    if (!analyser) { chunkVoiced = true; anyVoiced = true; } // без анализатора тишину не различаем — шлём всё

    try {
      try {
        rec = new env.MediaRecorder(stream, { audioBitsPerSecond: AUDIO_BPS });
      } catch {
        // движок не принял настройку — пишем как умеет (финал > ~177 с на 192k получит 413, черновик останется)
        rec = new env.MediaRecorder(stream);
      }
    } catch (err) {
      trace('recorder-error', err?.name || '');
      release();
      set({ phase: PHASE.IDLE, field: null, unavailable: true });
      return;
    }
    // ⚠️ ТИП ЗАПИСИ ЧИТАЕТСЯ ПОСЛЕ `start()`: Chrome отдаёт пустой `mimeType`, пока рекордер не запущен
    // (замер в Chromium, Г-4), — до старта контейнер не определился бы и куски со второго не склеились.
    const learnType = (fallback = '') => {
      if (mime) return;
      mime = rec.mimeType || fallback || '';
      container = containerOf(mime);
    };
    mime = ''; container = null;
    let k = 0;
    rec.ondataavailable = (e) => {
      trace('dataavailable', e.data?.size ?? 0, e.data?.type ?? '', rec.state);
      // Сторож уже закрыл эту запись (или её отменили) — поздний кусок никуда не шлём.
      if (myGen !== gen) return;
      if (!e.data || !e.data.size) return;
      const voiced = chunkVoiced;
      chunkVoiced = !analyser;
      const idx = k; k += 1;
      learnType(e.data.type);
      // ⚠️ Задача куска регистрируется СИНХРОННО: чтение байтов асинхронно, и без этого `finish` при быстром сбое
      // финала не дождался бы куска, пришедшего на «стоп», — дыра не засчиталась бы, черновик пропал бы (тикет повторов).
      chunkJobs.push(onChunk(e.data, idx, voiced, field, myGen));
    };
    rec.onstop = () => { trace('onstop', rec.state); finish(field, myGen); };
    rec.onerror = (e) => trace('onerror', e?.error?.name || '');

    const t = now();
    lastVoiceAt = t;
    // Отпустили до старта (системное окно разрешения перебило нажатие) — это тап: запись идёт.
    tapMode = !pressed;
    rec.start(CHUNK_MS);
    learnType();
    ticker = setIntervalFn(tick, TICK_MS);
    set({ phase: PHASE.RECORDING, startedAt: t });
    onStart(field);
  }

  /** @param {'tap'|'release'|'silence'|'cap'|'limit'|'leave'} [why] — только для трассы */
  function stop(why = 'leave') {
    trace('stop', why, st.phase, rec?.state ?? '');
    if (st.phase === PHASE.ASKING) { gen += 1; release(); set({ phase: PHASE.IDLE, field: null }); return; }
    if (st.phase !== PHASE.RECORDING) return;
    const field = st.field;
    const myGen = gen;
    const seconds = (now() - (st.startedAt ?? now())) / 1000;
    set({ phase: PHASE.FINISHING });
    if (ticker !== null) { clearIntervalFn(ticker); ticker = null; }
    // ⭐ Сторож — ДО `rec.stop()`: что бы ни сделал рекордер дальше (не отдал `onstop`, бросил), покой придёт по часам.
    if (guard !== null) clearTimeoutFn(guard);
    guard = setTimeoutFn(() => giveUp(field, myGen), finalWaitMs(seconds) + FINISH_GRACE_MS);
    try { rec.stop(); } catch (err) { trace('stop-threw', err?.name || ''); finish(field, myGen); }
  }

  /** Сторож сработал: запись этого поколения так и не вернулась в покой — покой силой. */
  function giveUp(field, myGen) {
    guard = null;
    if (myGen !== gen || st.phase !== PHASE.FINISHING) return;
    trace('guard', rec?.state ?? '', parts.length, chunkJobs.length);
    flushDrafts(field, myGen); // пришедшие черновики остаются в поле
    gen += 1; // поздние ответы этой записи больше ничего не трогают
    try { if (rec?.state !== 'inactive') rec?.stop?.(); } catch { /* */ }
    release();
    parts = []; chunkJobs = [];
    onFinal(field, null, { hole });
    set({ phase: PHASE.IDLE, field: null, startedAt: null });
  }

  async function finish(field, myGen) {
    // Сторож уже вернул эту запись в покой (или началась новая) — общие ресурсы не наши, не трогаем.
    if (myGen !== gen) return;
    const seconds = (now() - (st.startedAt ?? now())) / 1000;
    release();
    // Дождаться последнего `dataavailable` (он приходит до `onstop`, но `arrayBuffer` асинхронный).
    await Promise.resolve();
    const whole = parts.length ? new env.Blob(parts, { type: mime }) : null;
    parts = [];
    let text = null;
    if (whole && anyVoiced) {
      const { pass, stages } = context();
      trace('final', whole.size ?? 0, Math.round(seconds));
      const r = await transcribe({ audio: whole, mimeType: mime, kind: 'final', pass, stages, seconds, level: levelOf(levels) });
      trace('final-reply', r.ok ? 'ok' : r.limit ? 'limit' : 'failed');
      if (r.limit) markLimit(field);
      if (r.ok) text = r.text;
    }
    if (myGen !== gen) return;
    if (text !== null) {
      finalApplied = true;
      onFinal(field, text, { hole: false });
    } else {
      // Финал не пришёл — молча остаётся черновик; доливаем куски, которые ещё в пути.
      await Promise.allSettled(chunkJobs);
      flushDrafts(field, myGen);
      // ⭐ Строка сбоя у поля — только если дыра осталась ПОСЛЕ финала: кусок сдался И финал не пришёл (`s995.d1` п. 6).
      onFinal(field, null, { hole });
    }
    chunkJobs = [];
    if (myGen === gen) {
      if (guard !== null) { clearTimeoutFn(guard); guard = null; }
      set({ phase: PHASE.IDLE, field: null, startedAt: null });
    }
  }

  return {
    get state() { return { ...st }; },
    /** Нажатие на микрофон поля. */
    press(field) {
      if (st.phase === PHASE.RECORDING || st.phase === PHASE.ASKING) {
        // второе нажатие во время записи — «стоп» (кнопка показывает стоп)
        if (st.field === field) stop('tap');
        return;
      }
      pressed = true;
      pressAt = now();
      start(field);
    },
    /** Отпускание. Держали дольше 0,5 с после старта записи — стоп; иначе это был тап. */
    release() {
      if (!pressed) return;
      pressed = false;
      if (st.phase !== PHASE.RECORDING || tapMode) return;
      const since = now() - Math.max(pressAt, st.startedAt ?? pressAt);
      if (since >= HOLD_MS) stop('release');
      else tapMode = true;
    },
    /** Клавиатура / программный клик: переключатель. */
    toggle(field) {
      if (st.phase === PHASE.RECORDING || st.phase === PHASE.ASKING) { if (st.field === field) stop('tap'); return; }
      pressed = false;
      start(field);
    },
    stop,
    destroy() { gen += 1; if (guard !== null) { clearTimeoutFn(guard); guard = null; } try { rec?.state !== 'inactive' && rec?.stop?.(); } catch { /* */ } release(); },
  };
}
